import React, { useState, useEffect, useCallback } from 'react';
import { creditHistoryAPI } from '../../api/client';
import { X, History, TrendingUp, TrendingDown, Coins, Calendar, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const CreditHistoryModal = ({ isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [currentCredits, setCurrentCredits] = useState({});
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await creditHistoryAPI.getMyHistory(100);
      setHistory(response.data.transactions || []);
      setCurrentCredits(response.data.current_credits || {});
    } catch (error) {
      console.error('Failed to fetch credit history:', error);
      toast.error('Credit history লোড করতে সমস্যা হয়েছে');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      // Animate in
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, fetchHistory]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!isOpen) return null;

  const getTransactionIcon = (type, amount) => {
    if (amount > 0) {
      return <TrendingUp className="h-5 w-5 text-green-500" />;
    } else {
      return <TrendingDown className="h-5 w-5 text-red-500" />;
    }
  };

  const getTransactionBadge = (type) => {
    const badges = {
      'BOOKING_DEDUCT': { label: 'Booking', className: 'bg-blue-100 text-blue-800' },
      'CANCELLATION_REFUND': { label: 'Refund', className: 'bg-green-100 text-green-800' },
      'ADMIN_ADJUSTMENT': { label: 'Institute', className: 'bg-purple-100 text-purple-800' },
      'CREDIT_ADD': { label: 'Added', className: 'bg-green-100 text-green-800' },
      'CREDIT_DEDUCT': { label: 'Deducted', className: 'bg-red-100 text-red-800' },
    };
    const badge = badges[type] || { label: type, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={badge.className}>{badge.label}</Badge>;
  };

  const getModuleBadge = (module) => {
    const colors = {
      'LISTENING': 'bg-blue-500',
      'READING': 'bg-green-500',
      'WRITING': 'bg-yellow-500',
      'SPEAKING': 'bg-purple-500',
      'MOCK': 'bg-red-500',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs text-white ${colors[module] || 'bg-gray-500'}`}>
        {module}
      </span>
    );
  };

  // Format description to show "Institute" instead of admin/moderator name
  const formatDescription = (tx) => {
    let desc = tx.description;
    // Replace "Credit added by [Name]:" or "Credit deducted by [Name]:" with "Institute:"
    desc = desc.replace(/Credit added by [^:]+:/, 'Institute:');
    desc = desc.replace(/Credit deducted by [^:]+:/, 'Institute:');
    return desc;
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        isVisible ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden transform transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <History className="h-6 w-6" />
              <div>
                <h2 className="text-lg font-semibold">Credit History</h2>
                <p className="text-sm text-blue-100 font-bangla">আপনার সকল ক্রেডিট লেনদেনের ইতিহাস</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 rounded-full"
              onClick={handleClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Current Credits Summary */}
          <div className="mt-4 grid grid-cols-5 gap-2">
            {['listening', 'reading', 'writing', 'speaking', 'mock'].map((module) => (
              <div key={module} className="bg-white/10 rounded-lg p-2 text-center">
                <p className="text-xs text-blue-100 capitalize">{module}</p>
                <p className="text-lg font-bold">{currentCredits[module] || 0}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Coins className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg font-bangla">কোনো Credit History নেই</p>
              <p className="text-gray-400 text-sm mt-2 font-bangla">
                আপনার সকল credit লেনদেন এখানে দেখা যাবে। নতুন আপডেটের পরে history তৈরি হবে।
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {history.map((tx) => (
                <div key={tx.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getTransactionIcon(tx.type, tx.amount)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {getTransactionBadge(tx.type)}
                        {tx.module && getModuleBadge(tx.module)}
                      </div>
                      
                      <p className="text-sm text-gray-700">{formatDescription(tx)}</p>
                      
                      {tx.exam_date && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1 font-bangla">
                          <Calendar className="h-3 w-3" />
                          পরীক্ষার তারিখ: {tx.exam_date}
                        </p>
                      )}
                      
                      {/* Show "Institute" instead of admin/moderator name */}
                      {tx.performed_by_name && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1 font-bangla">
                          <Building2 className="h-3 w-3" />
                          Institute দ্বারা
                        </p>
                      )}
                      
                      <p className="text-xs text-gray-400 mt-1">
                        {tx.created_at}
                      </p>
                    </div>

                    {/* Amount & Balance */}
                    <div className="flex-shrink-0 text-right">
                      <p className={`font-bold text-lg ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount}
                      </p>
                      <p className="text-xs text-gray-500 font-bangla">
                        বাকি: <span className="font-medium">{tx.balance_after}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t p-3">
          <div className="text-center space-y-1">
            <p className="text-xs text-gray-600 font-bangla">
              📌 এখানে যা দেখা যাবে:
            </p>
            <p className="text-xs text-gray-500 font-bangla">
              • Exam Booking করলে Credit কাটার রেকর্ড
            </p>
            <p className="text-xs text-gray-500 font-bangla">
              • Institute কর্তৃক Credit যোগ/বিয়োগের রেকর্ড
            </p>
            <p className="text-xs text-gray-400 font-bangla mt-2">
              ⓘ এই ফিচারটি ৩০ ডিসেম্বর ২০২৫ থেকে চালু হয়েছে। এর আগের লেনদেন এখানে দেখা যাবে না।
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditHistoryModal;
